import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getEmpresas, getUsers, deleteUser, BACKEND_URL } from "../../api/api";
import UserFormModal from "../../components/forms/UserFormModal";
import ControlHorarios from "./ControlHorarios";
import { useParams, useNavigate } from "react-router-dom";
import logoRosoco from "../../assets/images/RoscoTransparente.gif";

export default function EmpresaDetail() {
  const { id } = useParams();
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [modalUser, setModalUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [section, setSection] = useState("usuarios");
  const [busqueda, setBusqueda] = useState("");
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en mobile

  const handleLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const empresas = await getEmpresas(token);
        setEmpresa(empresas.find((e) => e._id === id) || null);
        const users = await getUsers(token);
        setUsuarios(users.filter((u) => u.empresa && u.empresa._id === id));
      } catch (err) {
      }
    };
    if (token) load();
    // eslint-disable-next-line
  }, [id, token]);

  // Cerrar sidebar al cambiar de sección en mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [section]);

  useEffect(() => {
    // Filtrar usuarios de la empresa
    const filtrados = usuarios.filter((u) => {
      const textoCompleto = `${u.nombre} ${u.apellidos} ${u.email}`.toLowerCase();
      return textoCompleto.includes(busqueda.toLowerCase());
    });
    setUsuariosFiltrados(filtrados);
  }, [busqueda, usuarios]);

  const refresh = async () => {
    const users = await getUsers(token);
    setUsuarios(users.filter((u) => u.empresa && u.empresa._id === id));
  };

  const handleDelete = async (userId) => {
    await deleteUser(token, userId);
    setConfirmDelete(null);
    refresh();
  };

  const menuItems = [
    {
      id: "volver",
      label: "Volver",
      icon: "fa-arrow-left",
      submenu: [
        { id: "volver", label: "Volver a Empresas", icon: "fa-arrow-left", action: () => navigate("/global-admin") }
      ]
    },
    {
      id: "empresa",
      label: empresa?.nombre || "Empresa",
      icon: "fa-building",
      submenu: [
        { id: "usuarios", label: "Usuarios", icon: "fa-users" },
        { id: "fichajes", label: "Fichajes", icon: "fa-clipboard-list" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Superior */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 md:px-6 py-4">
          {/* Logo y Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-700 hover:text-gray-900 transition-colors"
            >
              <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
            </button>
            <div className="flex items-center gap-3">
              <img 
                src={logoRosoco} 
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-gray-800">{empresa?.nombre || 'Empresa'}</h1>
                <p className="text-sm text-gray-500">Panel de Administración Global</p>
              </div>
            </div>
          </div>

          {/* Usuario y Logout */}
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm font-semibold text-gray-800">{user?.nombre}</p>
              <p className="text-xs text-gray-500">Administrador Global</p>
            </div>
            {user?.imagenPerfil && (
              <img
                src={`${BACKEND_URL}${user.imagenPerfil}`}
                alt={user.nombre}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
              />
            )}
            <button
              onClick={() => setShowLogoutModal(true)}
              className="hidden md:flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg transition font-medium"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed top-[73px] left-0 h-[calc(100vh-73px)] bg-white border-r border-gray-200 transition-transform duration-300 z-30 w-72 overflow-y-auto ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => (
              <div key={item.id}>
                <div className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <i className={`fas ${item.icon} mr-2`}></i>
                  {item.label}
                </div>
                <div className="space-y-1">
                  {item.submenu.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => {
                        if (subItem.action) {
                          subItem.action();
                        } else {
                          setSection(subItem.id);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        section === subItem.id
                          ? 'bg-gray-800 text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`fas ${subItem.icon} text-lg`}></i>
                      <span className="font-medium">{subItem.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer del Sidebar */}
          <div className="p-4 mt-auto border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              <div className="mb-1">Desarrollado por</div>
              <a 
                href="https://www.faecagranada.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 font-semibold transition flex items-center justify-center gap-1"
              >
                FAECA Granada
                <i className="fas fa-external-link-alt text-xs"></i>
              </a>
            </div>
          </div>
        </aside>

        {/* Overlay para cerrar sidebar en mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Contenido Principal */}
        <main className={`flex-1 p-4 md:p-6 transition-all duration-300 overflow-hidden ${
          sidebarOpen ? 'md:ml-72' : 'ml-0'
        }`}>

        {section === "usuarios" && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="fas fa-users"></i>
                Usuarios
              </h3>
              <button
                onClick={() => setModalUser({ empresa: id })}
                className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition flex items-center gap-2 shadow-md font-medium"
              >
                <i className="fas fa-user-plus"></i>
                Añadir usuario
              </button>
            </div>

            {/* BARRA DE BÚSQUEDA */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-md">
              <div className="relative">
                <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Buscar por nombre, apellidos o email..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>
              {busqueda && (
                <p className="text-sm text-gray-600 mt-2 flex items-center gap-2">
                  <i className="fas fa-info-circle text-gray-500"></i>
                  Se encontraron <strong>{usuariosFiltrados.length}</strong> usuario(s)
                </p>
              )}
            </div>

            {/* GRID: 1 / 2 / 3 columnas (responsive) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {usuariosFiltrados.map((u) => (
                <div
                  key={u._id}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-gray-300 overflow-hidden"
                >
                  {/* Header con imagen de fondo */}
                  <div className="relative h-24 bg-gradient-to-br from-gray-700 to-gray-800">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                  </div>

                  {/* Avatar centrado sobre el header */}
                  <div className="relative px-5 pb-5">
                    <div className="flex justify-center -mt-12 mb-4">
                      <img
                        src={
                          u.imagenPerfil
                            ? `${BACKEND_URL}${u.imagenPerfil}`
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                u.nombre + " " + u.apellidos
                              )}&background=374151&color=fff&size=128`
                        }
                        alt={u.nombre}
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    </div>

                    {/* Info del usuario */}
                    <div className="text-center mb-4">
                      <h3 className="font-bold text-lg text-gray-800 mb-1">
                        {u.nombre} {u.apellidos}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{u.email}</p>
                      
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <i className={`fas ${u.role === 'admin' ? 'fa-user-shield' : u.role === 'global_admin' ? 'fa-crown' : 'fa-user'}`}></i>
                          {u.role === 'admin' ? 'Admin' : u.role === 'global_admin' ? 'Global Admin' : 'Trabajador'}
                        </span>
                        
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          u.habilitado 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <i className={`fas ${u.habilitado ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                          {u.habilitado ? 'Activo' : 'Deshabilitado'}
                        </span>
                      </div>
                    </div>

                    {/* Separador */}
                    <div className="border-t border-gray-100 mb-4"></div>

                    {/* Acciones */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setModalUser(u)}
                        className="flex-1 flex items-center justify-center gap-2 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 px-3 py-2 rounded-lg transition text-sm font-medium"
                        title="Editar"
                      >
                        <i className="fas fa-edit"></i>
                        Editar
                      </button>

                      <button
                        onClick={() => setConfirmDelete(u._id)}
                        className="flex items-center justify-center bg-red-50 text-red-600 hover:bg-red-100 w-10 h-10 rounded-lg transition"
                        title="Eliminar"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {section === "fichajes" && <ControlHorarios />}

        {/* modal edición/creación */}
        {modalUser && (
          <UserFormModal
            user={modalUser._id ? modalUser : { empresa: id }}
            onClose={() => {
              setModalUser(null);
              refresh();
            }}
            empresaId={id}
          />
        )}

        {/* modal confirmación borrado */}
        {confirmDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Confirmar eliminación
                </h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                ¿Estás seguro de que deseas eliminar este usuario? Esta acción <strong>no se podrá deshacer</strong>.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete)}
                  className="flex-1 bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 shadow-md"
                >
                  <i className="fas fa-trash-alt"></i>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      </div>

      {/* Modal de confirmación de logout */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="fas fa-sign-out-alt text-red-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                Cerrar sesión
              </h3>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              ¿Estás seguro de que quieres salir? Tu sesión actual se cerrará.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 shadow-md"
              >
                <i className="fas fa-sign-out-alt"></i>
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botón flotante de logout en mobile */}
      <button
        onClick={() => setShowLogoutModal(true)}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-red-600 text-white rounded-full shadow-2xl flex items-center justify-center z-30 hover:scale-110 transition-transform"
      >
        <i className="fas fa-sign-out-alt text-xl"></i>
      </button>
    </div>
  );
}
